export const IDENTITY_FIELDS = ['name', 'phone', 'email', 'company', 'role'] as const;
export type IdentityField = (typeof IDENTITY_FIELDS)[number];
export interface IdentityAskPayload {
  askId: string;
  field: IdentityField;
  revision: number;
  status: 'asked' | 'proposed' | 'resolved';
  mandatory: boolean;
  confirmRequired: boolean;
  value?: string;
  displayValue?: string;
  outcome?: 'confirmed' | 'declined';
}
export interface IdentityAskState extends IdentityAskPayload {
  revisions: Partial<Record<IdentityField, { revision: number; terminal: boolean }>>;
}

/** Keep terminal revisions even after the composer advances to another field. */
export function reduceIdentityAsk(current: IdentityAskState | null, raw: unknown): IdentityAskState | null {
  if (!raw || typeof raw !== 'object') return current;
  const p = raw as IdentityAskPayload;
  if (!IDENTITY_FIELDS.includes(p.field) || !Number.isSafeInteger(p.revision) || p.revision < 1 ||
      p.askId !== `${p.field}:${p.revision}` || !['asked', 'proposed', 'resolved'].includes(p.status) ||
      typeof p.mandatory !== 'boolean' || typeof p.confirmRequired !== 'boolean') return current;
  if (p.status === 'proposed' && (typeof p.value !== 'string' || typeof p.displayValue !== 'string' ||
      p.value.length > 320 || p.displayValue.length > 320)) return current;
  if (p.status === 'resolved' && (!['confirmed', 'declined'].includes(p.outcome || '') ||
      (p.value !== undefined && (typeof p.value !== 'string' || p.value.length > 320)))) return current;
  const prior = current?.revisions[p.field];
  if (prior && (p.revision < prior.revision || (p.revision === prior.revision && prior.terminal))) return current;
  if (current?.field === p.field && current.revision === p.revision &&
      (current.status === p.status || (current.status === 'proposed' && p.status === 'asked'))) return current;
  const revisions = { ...current?.revisions, [p.field]: { revision: p.revision, terminal: p.status === 'resolved' } };
  if (p.status === 'resolved' && current && current.field !== p.field) return { ...current, revisions };
  return {
    askId: p.askId, field: p.field, revision: p.revision, status: p.status,
    mandatory: p.mandatory, confirmRequired: p.confirmRequired,
    ...(p.status === 'proposed' ? { value: p.value, displayValue: p.displayValue } : {}),
    ...(p.status === 'resolved' ? { outcome: p.outcome, ...(p.outcome === 'confirmed' && typeof p.value === 'string' ? { value: p.value } : {}) } : {}), revisions,
  };
}

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const PHONE_LOOSE_RE = /^\+?[\d\s().-]{6,40}$/;
export function classifyComposerSubmit(field: IdentityField, text: string): 'confirm' | 'text' {
  const value = text.trim();
  return (field === 'phone' && PHONE_LOOSE_RE.test(value) && (value.match(/\d/g)?.length ?? 0) >= 6) ||
    (field === 'email' && value.length <= 320 && EMAIL_RE.test(value)) ? 'confirm' : 'text';
}
